import { useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { API_BASE } from "@/api/client";
import { useProjects } from "@/api/hooks";
import { Icon } from "@/components/Icon";
import { Button, cx, Segmented, Select } from "@/components/ui";
import { useCopy } from "@/lib/useCopy";

type Os = "unix" | "windows";

/** Step-by-step setup of the mixla CLI and the Claude Code skill, shown above the token list. */
export function CliSetupGuide({ defaultOpen, onCreateToken }: { defaultOpen: boolean; onCreateToken: () => void }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(defaultOpen);
  const [os, setOs] = useState<Os>(() => (navigator.userAgent.includes("Windows") ? "windows" : "unix"));
  const origin = window.location.origin;
  const win = os === "windows";

  return (
    <div className="flex flex-col gap-4 rounded-[var(--radius-ctl)] border border-border bg-surface-2 p-4">
      <button
        type="button"
        aria-expanded={open}
        aria-controls="cli-setup-steps"
        onClick={() => setOpen((o) => !o)}
        className="-m-1 flex items-center gap-2 rounded-[var(--radius-ctl)] p-1 text-left font-semibold"
      >
        <Icon name={open ? "down" : "right"} size={14} />
        <Icon name="terminal" size={16} className="text-accent" />
        {t("cliGuide.title")}
        <span className="ml-auto text-sm font-normal text-muted">{t("cliGuide.time")}</span>
      </button>

      {open && (
        <div id="cli-setup-steps" className="flex flex-col gap-5">
          <Segmented
            label={t("cliGuide.os")}
            value={os}
            onChange={setOs}
            options={[
              { value: "unix", label: "macOS / Linux" },
              { value: "windows", label: "Windows" },
            ]}
          />
          <ol className="m-0 flex list-none flex-col gap-5 p-0">
            <Step n={1} title={t("cliGuide.install.title")} body={t(win ? "cliGuide.install.bodyWindows" : "cliGuide.install.body")}>
              <Cmd win={win} cmd={win ? `irm ${origin}/api/v1/cli/install.ps1 | iex` : `curl -fsSL ${origin}/api/v1/cli/install.sh | sh`} />
              <Cmd win={win} cmd="mixla --version" />
            </Step>
            <Step n={2} title={t("cliGuide.token.title")} body={t("cliGuide.token.body")}>
              <div>
                <Button size="sm" icon="key" onClick={onCreateToken}>
                  {t("cliGuide.token.action")}
                </Button>
              </div>
            </Step>
            <Step n={3} title={t("cliGuide.login.title")} body={t("cliGuide.login.body")}>
              <Cmd win={win} cmd={`mixla login --url ${origin} --token mx_…`} />
              <Cmd win={win} cmd="mixla whoami" />
            </Step>
            <Step n={4} title={t("cliGuide.skill.title")} body={t("cliGuide.skill.body")}>
              <SkillScope win={win} />
            </Step>
            <Step n={5} title={t("cliGuide.link.title")} body={t("cliGuide.link.body")}>
              <LinkProject win={win} />
            </Step>
            <Step n={6} title={t("cliGuide.use.title")} body={t("cliGuide.use.body")}>
              <ul className="m-0 flex flex-col gap-1.5 pl-4 text-sm text-text-2">
                <li>“{t("cliGuide.use.todo")}”</li>
                <li>“{t("cliGuide.use.push")}”</li>
                <li>“{t("cliGuide.use.fix")}”</li>
                <li>“{t("cliGuide.use.bug")}”</li>
              </ul>
            </Step>
            <li className="text-sm text-muted">{t("cliGuide.trouble")}</li>
          </ol>
        </div>
      )}
    </div>
  );
}

type Scope = "global" | "repo";

/** Where the skill goes: every repository on this computer (home folder), or one repository (committed with it). */
function SkillScope({ win }: { win: boolean }) {
  const { t } = useTranslation();
  const [scope, setScope] = useState<Scope>("global");
  const options: { value: Scope; label: string; hint: string }[] = [
    { value: "global", label: t("cliGuide.skill.global"), hint: t("cliGuide.skill.globalHint") },
    { value: "repo", label: t("cliGuide.skill.repo"), hint: t("cliGuide.skill.repoHint") },
  ];
  return (
    <>
      <fieldset className="m-0 grid gap-2 border-0 p-0 sm:grid-cols-2">
        <legend className="sr-only">{t("cliGuide.skill.scope")}</legend>
        {options.map((o) => (
          <label
            key={o.value}
            className={cx(
              "flex cursor-pointer items-start gap-2.5 rounded-[var(--radius-ctl)] border bg-surface px-3 py-2.5",
              scope === o.value ? "border-accent ring-1 ring-accent" : "border-border-strong hover:bg-surface-2",
            )}
          >
            <input
              type="radio"
              name="skill-scope"
              value={o.value}
              checked={scope === o.value}
              onChange={() => setScope(o.value)}
              className="mt-1 accent-[var(--accent)]"
            />
            <span className="flex flex-col gap-0.5">
              <span className="text-sm font-semibold">
                {o.label}
                {o.value === "global" && <span className="ml-1.5 font-normal text-muted">· {t("cliGuide.skill.globalTag")}</span>}
              </span>
              <span className="text-xs text-muted">{o.hint}</span>
            </span>
          </label>
        ))}
      </fieldset>
      {scope === "global" ? (
        <Cmd win={win} cmd="mixla skill install --global" />
      ) : (
        <>
          <Cmd win={win} cmd={t("cliGuide.skill.repoCd")} />
          <Cmd win={win} cmd="mixla skill install" />
        </>
      )}
      <CopySkill path={skillPath(scope, win)} />
    </>
  );
}

function skillPath(scope: Scope, win: boolean): string {
  const rel = win ? ".claude\\skills\\mixla\\SKILL.md" : ".claude/skills/mixla/SKILL.md";
  if (scope === "repo") return rel;
  return win ? `%USERPROFILE%\\${rel}` : `~/${rel}`;
}

/** The skill file itself, for setting it up without the CLI. */
function CopySkill({ path }: { path: string }) {
  const { t } = useTranslation();
  const { copied, copy } = useCopy();
  // Loaded up front: browsers only allow clipboard writes right inside the click.
  const skill = useQuery({
    queryKey: ["cli", "skill"],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/cli/skill.md`);
      if (!res.ok) throw new Error(res.statusText);
      return res.text();
    },
    staleTime: Infinity,
  });
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-xs text-muted">
      <Button
        size="sm"
        icon={copied ? "check" : "copy"}
        loading={skill.isPending}
        disabled={!skill.data}
        onClick={() => skill.data && void copy(skill.data)}
      >
        {copied ? t("common.copied") : t("cliGuide.skill.copy")}
      </Button>
      <span className="min-w-0 flex-[1_1_240px]">
        {t("cliGuide.skill.copyHint")} <code className="font-mono break-all text-text-2">{path}</code>
      </span>
    </div>
  );
}

/** `.mixla.toml` for a repository, with the user's own project key instead of an example. */
function LinkProject({ win }: { win: boolean }) {
  const { t } = useTranslation();
  const projects = useProjects();
  const keys = (projects.data ?? []).map((p) => p.key);
  const [picked, setPicked] = useState("");
  const key = picked || keys[0] || "PAY";
  return (
    <>
      {keys.length > 1 && (
        <label className="flex items-center gap-2 text-sm">
          <span className="text-text-2">{t("cliGuide.link.project")}</span>
          <Select value={key} onChange={(e) => setPicked(e.target.value)} className="w-auto h-8 font-mono">
            {keys.map((k) => (
              <option key={k} value={k}>
                {k}
              </option>
            ))}
          </Select>
        </label>
      )}
      <Cmd win={win} cmd={win ? `Set-Content .mixla.toml 'project = "${key}"'` : `echo 'project = "${key}"' > .mixla.toml`} />
    </>
  );
}

function Step({ n, title, body, children }: { n: number; title: string; body: string; children: ReactNode }) {
  return (
    <li className="grid grid-cols-[28px_minmax(0,1fr)] gap-x-3 gap-y-2">
      <span
        aria-hidden="true"
        className="inline-flex size-7 items-center justify-center rounded-full bg-accent-soft text-sm font-semibold text-accent"
      >
        {n}
      </span>
      <div className="flex flex-col gap-1">
        <h3 className="m-0 text-sm font-semibold">
          <span className="sr-only">{n}. </span>
          {title}
        </h3>
        <p className="m-0 text-sm text-text-2">{body}</p>
      </div>
      <div className="col-start-2 flex flex-col gap-2">{children}</div>
    </li>
  );
}

function Cmd({ cmd, win }: { cmd: string; win: boolean }) {
  const { t } = useTranslation();
  const copy = useCopy();
  return (
    <div className="flex items-center gap-2">
      <code className="min-w-0 flex-1 rounded-md border border-border bg-surface px-2.5 py-2 font-mono text-xs break-all">
        <span aria-hidden="true" className="text-muted select-none">
          {win ? "PS> " : "$ "}
        </span>
        {cmd}
      </code>
      <Button size="sm" icon="copy" aria-label={`${t("common.copy")}: ${cmd}`} onClick={() => void copy.copy(cmd)}>
        {copy.copied ? t("common.copied") : t("common.copy")}
      </Button>
    </div>
  );
}
